// frontend/src/components/auth/MfaOnboardingModal.tsx
import { useState, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { api } from '@/services/api';
import { ShieldCheck, CheckCircle2, Loader2 } from 'lucide-react';
import { MonoLabel } from '@/components/editorial';

type ModalState = 'idle' | 'sending' | 'awaiting-code' | 'verifying' | 'success';

export function MfaOnboardingModal() {
  const { t } = useTranslation('auth');
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<ModalState>('idle');
  const [maskedDest, setMaskedDest] = useState('');
  const [code, setCode] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    const flag = localStorage.getItem('mfa_onboarding_pending');
    if (flag) {
      localStorage.removeItem('mfa_onboarding_pending');
      setOpen(true);
    }
  }, []);

  useEffect(() => {
    if (state !== 'success') return;
    const timer = setTimeout(() => setOpen(false), 2000);
    return () => clearTimeout(timer);
  }, [state]);

  const handleEnable = async () => {
    setState('sending');
    try {
      const r = await api.mfa.enrollStart('email');
      if (r.status === 'success' && r.data) {
        setMaskedDest(r.data.sent_to_masked);
        setCode('');
        setState('awaiting-code');
      }
    } catch (err) {
      console.error('MFA enroll start failed:', err);
      toast({
        variant: 'destructive',
        title: t('mfaOnboarding.sendFailedTitle'),
        description: t('mfaOnboarding.sendFailedDescription'),
      });
      setState('idle');
    }
  };

  const handleVerify = async () => {
    if (code.length !== 6) return;
    setState('verifying');
    try {
      const r = await api.mfa.enrollVerify('email', code);
      if (r.status === 'success') {
        setState('success');
      }
    } catch {
      toast({
        variant: 'destructive',
        title: t('mfaOnboarding.invalidTitle'),
        description: t('mfaOnboarding.invalidDescription'),
      });
      setState('awaiting-code');
    }
  };

  const handleSkip = () => {
    setOpen(false);
    toast({
      title: t('mfaOnboarding.skipTitle'),
      description: t('mfaOnboarding.skipDescription'),
    });
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-md flex flex-col p-0 gap-0 [&>button]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {state === 'success' ? (
          <div className="flex flex-col items-center text-center px-6 py-8 gap-3">
            <CheckCircle2 className="h-10 w-10 text-status-ok-fg" strokeWidth={1.5} />
            <DialogTitle
              className="font-serif text-[22px] font-medium leading-tight tracking-tight"
            >
              {t('mfaOnboarding.enabledTitle')}
            </DialogTitle>
            <DialogDescription
              className="text-body text-stevens-ink-3 leading-relaxed max-w-[36ch]"
            >
              {t('mfaOnboarding.enabledBody')}
            </DialogDescription>
            <MonoLabel className="block text-stevens-ink-3/80 mt-1">
              {t('mfaOnboarding.closing')}
            </MonoLabel>
          </div>
        ) : state === 'awaiting-code' || state === 'verifying' ? (
          <>
            <header className="flex items-start gap-3 px-6 pt-6 pb-4">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-stevens-indigo/10 text-stevens-indigo flex-shrink-0">
                <ShieldCheck className="h-4 w-4" strokeWidth={1.75} />
              </span>
              <div className="flex-1 min-w-0">
                <MonoLabel className="block mb-1">
                  {t('mfaOnboarding.eyebrow')}
                </MonoLabel>
                <DialogTitle
                  className="font-serif text-[22px] font-medium leading-tight tracking-tight"
                >
                  {t('mfaOnboarding.enterCodeTitle')}
                </DialogTitle>
              </div>
            </header>
            <div className="px-6 pb-2">
              <VisuallyHidden>
                <DialogDescription>{t('mfaOnboarding.enterCodeDescription')}</DialogDescription>
              </VisuallyHidden>
              <p className="text-body text-foreground/80 leading-relaxed mb-4">
                <Trans
                  t={t}
                  i18nKey="mfaOnboarding.sentTo"
                  values={{ destination: maskedDest }}
                  components={{ strong: <strong /> }}
                />
              </p>
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                autoFocus
                className="text-center font-mono text-stat-sm tracking-[0.4em]"
              />
            </div>
            <footer className="flex flex-col gap-2 px-6 pb-6 pt-3">
              <Button
                onClick={handleVerify}
                disabled={state === 'verifying' || code.length !== 6}
                variant="brand" className="w-full"
              >
                {state === 'verifying' ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('mfa.verifying')}</>
                ) : (
                  t('mfaOnboarding.verifyAndEnable')
                )}
              </Button>
              <Button variant="ghost" onClick={handleSkip} disabled={state === 'verifying'} className="w-full text-stevens-ink-3 hover:text-foreground">
                {t('mfaOnboarding.maybeLater')}
              </Button>
            </footer>
          </>
        ) : (
          <>
            <header className="flex items-start gap-3 px-6 pt-6 pb-4">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-stevens-indigo/10 text-stevens-indigo flex-shrink-0">
                <ShieldCheck className="h-4 w-4" strokeWidth={1.75} />
              </span>
              <div className="flex-1 min-w-0">
                <MonoLabel className="block mb-1">
                  {t('mfaOnboarding.eyebrow')}
                </MonoLabel>
                <DialogTitle
                  className="font-serif text-[22px] font-medium leading-tight tracking-tight"
                >
                  {t('mfaOnboarding.secureTitle')}
                </DialogTitle>
              </div>
            </header>
            <div className="px-6 pb-2">
              <VisuallyHidden>
                <DialogDescription>
                  {t('mfaOnboarding.secureBody')}
                </DialogDescription>
              </VisuallyHidden>
              <p
                className="text-body text-foreground/80 leading-relaxed mb-4"
              >
                {t('mfaOnboarding.secureBody')}
              </p>
              <div className="rounded-lg border border-stevens-indigo/15 bg-stevens-indigo/5 px-3.5 py-3 mb-2">
                <MonoLabel tone="indigo" className="block mb-1">
                  {t('mfaOnboarding.whyTitle')}
                </MonoLabel>
                <p className="text-body-sm text-foreground/85 leading-relaxed">
                  {t('mfaOnboarding.whyBody')}
                </p>
              </div>
            </div>
            <footer className="flex flex-col gap-2 px-6 pb-6 pt-3">
              <Button
                onClick={handleEnable}
                disabled={state === 'sending'}
                variant="brand" className="w-full"
              >
                {state === 'sending' ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('mfaOnboarding.sendingCode')}</>
                ) : (
                  t('mfaOnboarding.enable')
                )}
              </Button>
              <Button
                variant="ghost"
                onClick={handleSkip}
                disabled={state === 'sending'}
                className="w-full text-stevens-ink-3 hover:text-foreground"
              >
                {t('mfaOnboarding.maybeLater')}
              </Button>
            </footer>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
